"use client"

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { X } from "lucide-react"

import { ChipContent, chipClass } from "@/components/graphite/chip"
import { cn } from "@/lib/utils"

/**
 * A filter chip that opens a panel under itself.
 *
 * Everything that is the same about the date range and the two selects lives
 * here: the trigger and its three states, the panel, and the four ways a panel
 * has to be able to go away — Escape, a pointer outside, focus leaving, and a
 * second click on the chip. Written twice, these drift; the date panel would
 * dismiss on focus-out and the company one would not, and nobody would notice
 * until a keyboard user was left with a menu hanging over the page.
 *
 * The positioning is the other half. The ATS lives in a resizable pane, so a
 * panel hung from a chip runs off one side or the other — over the pane's own
 * drag handle, in the case that prompted this. It is measured once it is on
 * screen and slid back inside the row it belongs to, and measured again
 * whenever the room changes.
 *
 * What the panel *contains* is the caller's business: `children` is given a
 * `close` it can call when its work is done.
 */

/** Breathing room the panel keeps against the window's edge. */
const GUTTER = 8

export function ChipPopover({
  label,
  purpose,
  value,
  active,
  mono,
  onClear,
  clearLabel,
  haspopup,
  panelRole,
  panelLabel,
  panelClassName,
  onOpenChange,
  className,
  triggerClassName,
  children,
}: {
  /** The quiet prefix — "Fee". Omitted where the value names itself. */
  label?: string
  /**
   * What the chip filters, for a screen reader, where no visible label says it.
   * "All companies" tells you the value and nothing about the control; the fee
   * chip needs none of this because it wears the word "Fee".
   */
  purpose?: string
  /** What the filter is currently set to. */
  value: string
  active?: boolean
  /** Values are mono; a name is not a value. */
  mono?: boolean
  /**
   * Given only where the chip needs a way *off* that its panel does not already
   * offer. A select whose first option is "All companies" does not.
   */
  onClear?: () => void
  clearLabel?: string
  /** What the trigger promises it will open. */
  haspopup: "dialog" | "listbox"
  /**
   * Given only when the panel *is* the thing. A listbox puts the role on its
   * own list instead, so that the options are its direct children.
   */
  panelRole?: "dialog"
  panelLabel?: string
  panelClassName?: string
  /** Fires before the panel appears, so a caller can set up what it shows. */
  onOpenChange?: (open: boolean) => void
  className?: string
  triggerClassName?: string
  children: (helpers: { close: () => void }) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [offset, setOffset] = useState(0)
  const [minWidth, setMinWidth] = useState<number>()
  const [maxWidth, setMaxWidth] = useState<number>()

  const wrapper = useRef<HTMLSpanElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  /**
   * Slides the panel back inside the row it hangs from.
   *
   * Idempotent: the wanted position is computed from the chip, whose place does
   * not depend on the offset, so calling this again with an offset already
   * applied lands on the same answer.
   */
  const reposition = useCallback(() => {
    const box = panel.current?.getBoundingClientRect()
    const chip = trigger.current?.getBoundingClientRect()
    if (!box || !chip) return

    /*
      The row's *content* box, not the window: clamping to the content edge
      rather than the padding edge also lines the panel up with the cards above
      it.
    */
    const row = wrapper.current?.parentElement
    const rect = row?.getBoundingClientRect()
    const pad = row ? getComputedStyle(row) : undefined
    const left = Math.max(
      GUTTER,
      rect && pad ? rect.left + parseFloat(pad.paddingLeft) : GUTTER,
    )
    const right = Math.min(
      window.innerWidth - GUTTER,
      rect && pad ? rect.right - parseFloat(pad.paddingRight) : window.innerWidth,
    )

    /*
      Sliding it back inside is not enough on its own: a list of long company
      names sizes the panel to the longest of them, and a panel wider than the
      row cannot be slid anywhere that fits. So it is also given the row as a
      ceiling, which is what makes the labels' own truncation engage.
    */
    const room = right - left
    const width = Math.min(box.width, room)
    const wanted = Math.max(left, Math.min(chip.left, right - width))

    setOffset(Math.round(wanted - chip.left))
    setMaxWidth(Math.round(room))
    // A panel narrower than the chip it hangs from reads as a different object.
    setMinWidth(Math.min(Math.round(chip.width), Math.round(room)))
  }, [])

  useLayoutEffect(() => {
    if (!open) {
      /*
        Everything measured goes with the panel. A ceiling left over from a
        narrow row would otherwise be applied to the *next* opening before it
        has been measured, and the panel would come back still wearing a
        constraint that no longer applies.
      */
      setOffset(0)
      setMinWidth(undefined)
      setMaxWidth(undefined)
      return
    }
    reposition()

    /*
      And again whenever the room changes: the pane is dragged to resize, which
      moves the chip and narrows the row. The panel is watched too, because its
      own contents can change under it — a list opened while the placements are
      still loading grows when the company names arrive.

      This settles rather than oscillates: the ceiling comes from the row, so
      once it is applied the panel measures at or under it and the next pass
      computes the same numbers.
    */
    const observer = new ResizeObserver(reposition)
    const row = wrapper.current?.parentElement
    if (row) observer.observe(row)
    if (panel.current) observer.observe(panel.current)
    window.addEventListener("resize", reposition)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", reposition)
    }
  }, [open, reposition])

  const dismiss = useCallback(
    (returnFocus: boolean) => {
      setOpen(false)
      onOpenChange?.(false)
      if (returnFocus) trigger.current?.focus()
    },
    [onOpenChange],
  )

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss(true)
    }
    const outside = (event: Event) => {
      if (wrapper.current?.contains(event.target as Node)) return
      // Not the focus: the pointer or the keyboard has gone somewhere else on
      // purpose, and dragging it back would be the panel arguing.
      dismiss(false)
    }

    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("pointerdown", outside)
    document.addEventListener("focusin", outside)
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("pointerdown", outside)
      document.removeEventListener("focusin", outside)
    }
  }, [open, dismiss])

  return (
    <span ref={wrapper} className={cn(chipClass(active, "p-0"), className)}>
      <button
        ref={trigger}
        type="button"
        aria-haspopup={haspopup}
        aria-expanded={open}
        onClick={() => {
          if (open) return dismiss(false)
          onOpenChange?.(true)
          setOpen(true)
        }}
        className={cn(
          "inline-flex min-w-0 items-center gap-2 rounded-md py-2 pl-3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
          onClear && active ? "pr-1.5" : "pr-3",
          triggerClassName,
        )}
      >
        {purpose && <span className="sr-only">{purpose}</span>}
        <ChipContent label={label} value={value} active={active} open={open} mono={mono} />
      </button>

      {onClear && active && (
        <button
          type="button"
          aria-label={clearLabel ?? `Clear ${label ?? "filter"}`}
          onClick={() => {
            onClear()
            dismiss(false)
          }}
          className="mr-1.5 flex items-center rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
        >
          <X className="size-3" />
        </button>
      )}

      {open && (
        <div
          ref={panel}
          role={panelRole}
          aria-label={panelLabel}
          style={{ left: offset, minWidth, maxWidth }}
          className={cn(
            "absolute top-[calc(100%+6px)] z-30 rounded-[10px] border bg-card",
            "shadow-[0_12px_32px_-8px_rgba(0,0,0,0.45)]",
            panelClassName,
          )}
        >
          {children({ close: () => dismiss(true) })}
        </div>
      )}
    </span>
  )
}
